import React, { useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  GET_DIAMS_WITH_IMAGE,
  GET_DIAMOND_ANALYSES,
  SPLIT_FANCY_COLOR,
  tokenOptions,
} from "../../backend/graphql";
import { useAppSelector } from "../../app/hooks";
import { backendURL } from "../../constants";
import { isAnalyzed } from "../../backend/calibration";
import ColorValue from "../widgets/ColorValue";
import DiamondTags from "../widgets/DiamondTags";
import GroupTitle from "../widgets/GroupTitle";
import QuickFilter from "../widgets/QuickFilter";
import SectionPaper from "../widgets/SectionPaper";
import SpecRow from "../widgets/SpecRow";
import StoneSearch, { Picture } from "./StoneSearch";
import useDocumentTitle, { stoneName } from "../widgets/useDocumentTitle";
import { useDiamondOptions } from "./StoneSearch/useDiamondOptions";
import { formatColorQuery } from "../../tools";

// What the backend calls a part the color does not carry — offered as a search in its own right.
const NONE = "None";

// The stone's header, the color systems, the filters and the tabs above the gallery.
const HEADER_HEIGHT = 400;

// The stone the search is made from: the diamond header's own pieces, at the gallery's picture size
// and without the money — a price belongs to the stone's record, not to a search made from it.
const SourceStone = ({
  diamond,
  imageID,
}: {
  diamond: any;
  imageID?: string;
}) => (
  <SectionPaper
    sx={{ p: "14px 18px", border: 1, borderColor: "divider", borderRadius: 1 }}
  >
    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
      {imageID && (
        <Box
          component="img"
          src={`${backendURL}/image/thumbnail/${imageID}`}
          alt=""
          sx={{
            width: 128,
            height: 128,
            flex: "none",
            objectFit: "contain",
            bgcolor: "#fff",
            borderRadius: 1,
          }}
        />
      )}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 500, lineHeight: 1.2 }}>
          {diamond.source} {diamond.sKU}
        </Typography>
        <DiamondTags data={diamond} />
        <Divider sx={{ my: "14px" }} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1fr) 1px minmax(0,1fr)",
          }}
        >
          <Box sx={{ gridColumn: 1, pr: 3 }}>
            <GroupTitle>Stone</GroupTitle>
            <SpecRow label="Shape" value={diamond.shape} />
            <SpecRow label="Cut" value={diamond.cut} />
            <SpecRow label="Carat" value={diamond.carat} />
            <SpecRow label="Clarity" value={diamond.clarity} />
          </Box>
          <Box sx={{ gridColumn: 2, borderLeft: 1, borderColor: "divider" }} />
          <Box sx={{ gridColumn: 3, pl: 3 }}>
            <GroupTitle>Color</GroupTitle>
            <SpecRow
              label="GIA"
              value={<ColorValue label={diamond.color} colorKey={diamond.color} />}
            />
            <SpecRow
              label="Langerman"
              value={
                <ColorValue
                  label={diamond.langermanColor}
                  colorKey={formatColorQuery("Langerman", diamond.langermanColor)}
                />
              }
            />
            <SpecRow
              label="Flat"
              value={
                <ColorValue label={diamond.flat} colorKey={`Flat ${diamond.flat}`} />
              }
            />
            <SpecRow
              label="Melee"
              value={
                <ColorValue
                  label={diamond.melee}
                  colorKey={`Melee ${diamond.melee}`}
                />
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  </SectionPaper>
);

// The stones nearest one of our own, reached from Find similar on its analysis. The color it ranks
// by is the stone's, and each color system is a chip that says whether that system counts — the
// values are the stone's, so there is nothing to choose but which of them apply.
const SimilarStone = () => {
  const { id } = useParams();
  const token = useAppSelector((state) => state.auth.token);
  const options = useDiamondOptions();

  const [systems, setSystems] = useState({
    fancyIntensity: false,
    fancySaturation: false,
    fancyOvertone: false,
    fancyColor: false,
    langermanColor: false,
    flat: false,
    melee: false,
  });

  const { data: diamondData } = useQuery(GET_DIAMS_WITH_IMAGE, {
    ...tokenOptions(token),
    variables: { take: 1, skip: 0, filter: 'iD == "' + id + '"' },
  });

  // The stone's diamond analysis is where its measured color lives, and that color is what orders
  // the results.
  const { data: analysisData } = useQuery(GET_DIAMOND_ANALYSES, {
    ...tokenOptions(token),
    variables: { take: 1, skip: 0, filter: 'diamondID == "' + id + '"' },
  });

  const diamond = diamondData?.diamonds?.items[0];
  useDocumentTitle(stoneName(diamond), "Similar stones");

  // The stone's color in the three parts a search takes, split by the backend so the rule lives in
  // one place.
  const { data: splitData } = useQuery(SPLIT_FANCY_COLOR, {
    ...tokenOptions(token),
    variables: { color: diamond?.color },
    skip: !diamond?.color,
  });
  const parts = splitData?.splitFancyColor;
  const analysis = (analysisData?.diamondAnalyses?.items ?? []).find(
    (item: any) => item.oKH != null,
  );

  if (!diamond || !analysis) return <p>Loading...</p>;

  const pictureID = (picture: Picture) =>
    diamond.images?.find((image: any) =>
      picture === "original"
        ? isAnalyzed(image.calibration)
        : image.calibration?.endsWith("WebLike"),
    )?.id;

  const toggle = (system: keyof typeof systems) => (active: boolean) =>
    setSystems({ ...systems, [system]: !active });

  return (
    <StoneSearch
      options={options}
      headerHeight={HEADER_HEIGHT}
      excludeDiamondID={diamond.id}
      search={{
        okL: analysis.oKL,
        okC: analysis.oKC,
        okH: analysis.oKH,
        fancyIntensity: systems.fancyIntensity ? parts?.fancyIntensity : null,
        fancySaturation: systems.fancySaturation
          ? parts?.fancySaturation || NONE
          : null,
        fancyOvertone: systems.fancyOvertone ? parts?.fancyOvertone || NONE : null,
        fancyColor: systems.fancyColor ? parts?.fancyColor : null,
        langermanColor: systems.langermanColor ? diamond.langermanColor : null,
        flat: systems.flat ? diamond.flat : null,
        melee: systems.melee ? diamond.melee : null,
      }}
      colorArea={(picture) => (
        <SourceStone
          diamond={diamond}
          imageID={pictureID(picture) ?? pictureID("original")}
        />
      )}
      colorFilters={
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {/* A system the stone has no value for is not a search anyone can make, so it is not
              offered at all rather than offered as an empty one. */}
          {(
            [
              ["fancyIntensity", "Intensity", parts?.fancyIntensity],
              ["fancySaturation", "Saturation", parts?.fancySaturation || (parts ? NONE : null)],
              ["fancyOvertone", "Overtone", parts?.fancyOvertone || (parts ? NONE : null)],
              ["fancyColor", "Color", parts?.fancyColor],
              ["langermanColor", "Langerman color", diamond.langermanColor],
              ["flat", "Flat", diamond.flat],
              ["melee", "Melee", diamond.melee],
            ] as const
          )
            .filter(([, , value]) => value != null && value !== "")
            .map(([system, label, value]) => (
              <QuickFilter
                key={system}
                label={`${label}: ${value}`}
                active={systems[system]}
                toggle={toggle(system)}
              />
            ))}
        </Box>
      }
    />
  );
};

export default SimilarStone;
